<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Buyer Activity"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No data found for selected period"
      >
        <template #head(total_volume)>
          <button
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort('volume')"
          >
            <span>Total Volume</span>
            <span class="ml-2">{{ sortIcon('volume') }}</span>
          </button>
        </template>

        <template #head(delta_percent)>
          <button
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort('delta')"
          >
            <span>Delta %</span>
            <span class="ml-2">{{ sortIcon('delta') }}</span>
          </button>
        </template>

        <template #head(last_order_date)>
          <button
              type="button"
              class="report-sort-head btn btn-link p-0 d-inline-flex align-items-center"
              @click="toggleSort('last_order_date')"
          >
            <span>Last Order Date</span>
            <span class="ml-2">{{ sortIcon('last_order_date') }}</span>
          </button>
        </template>
      </b-table>

      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="d-flex flex-wrap align-items-center">
            <div class="mr-3 mb-2 mb-md-0">
              <strong class="text-uppercase">Start Date:</strong> {{ startDate }}
              <b-form-datepicker
                  v-model="filters.start_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onStartDateChanged"
              />
            </div>

            <div class="mr-3 mb-2 mb-md-0">
              <strong class="text-uppercase">End Date:</strong> {{ endDate }}
              <b-form-datepicker
                  v-model="filters.end_date"
                  class="ml-2"
                  :max="maxDate"
                  button-only
                  button-variant="primary"
                  @input="onEndDateChanged"
              />
            </div>
          </div>

          <div class="d-flex">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reset Filters"
                @click.prevent="resetFilters"
            >
              <b-icon-arrow-counterclockwise/>
            </b-btn>
          </div>
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import moment from "moment";
import {getFormattedPrice} from "../helpers";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportBuyerActivity",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    const query = this.$route.query;

    return {
      loading: true,
      report_data: [],
      filters: {
        start_date: query.start_date ?? moment().startOf('month').format('YYYY-MM-DD'),
        end_date: query.end_date ?? moment().format('YYYY-MM-DD'),
        sort: query.sort ?? 'volume',
        sort_direction: query.sort_direction ?? 'desc',
      },
    };
  },
  methods: {
    formatDelta(value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return '-';
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return '-';
      }

      return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}%`;
    },
    formatDate(value) {
      if (!value) {
        return '-';
      }

      return moment(value).format('L');
    },
    setQueryVar(key, value) {
      const query = {...this.$route.query};

      if (!value && typeof query[key] !== 'undefined') {
        delete query[key];
      } else if (value) {
        query[key] = value;
      }

      this.$router.replace({query});
    },
    onStartDateChanged(startDate) {
      if (moment(startDate).isAfter(moment(this.filters.end_date))) {
        this.filters.end_date = startDate;
      }

      this.loadReportData();
    },
    onEndDateChanged(endDate) {
      if (moment(endDate).isBefore(moment(this.filters.start_date))) {
        this.filters.start_date = endDate;
      }

      this.loadReportData();
    },
    loadReportData() {
      const reportUrl = API_ROUTES.ADMIN_REPORT_BUYERS_ACTIVITY;
      const params = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        sort: this.filters.sort,
        sort_direction: this.filters.sort_direction,
      };

      this.loading = true;

      this.$api.get(reportUrl, {params})
          .then(({data}) => {
            this.report_data = Array.isArray(data.report_data) ? data.report_data : [];
          })
          .catch(({response}) => {
            let message = 'Failed to load report';

            if (response && response.status === 422) {
              message = 'Some of your params were incorrect';
              this.resetFilters();
            }

            this.$bvToast.toast(message, {
              title: 'Error!',
              variant: 'danger'
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    resetFilters() {
      this.filters = {
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        sort: 'volume',
        sort_direction: 'desc',
      };

      this.loadReportData();
    },
    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const formatCsvMoney = (value) => {
        const numericValue = Number(value || 0);
        return `$${numericValue.toFixed(2)}`;
      };

      const formatCsvDelta = (value) => {
        if (value === null || typeof value === 'undefined' || value === '') {
          return '';
        }

        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) {
          return '';
        }

        return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}%`;
      };

      const formatCsvDate = (value) => {
        return value ? moment(value).format('YYYY-MM-DD') : '';
      };

      let csvContent = "data:text/csv;charset=utf-8,";
      const headers = [
        'Buyer',
        'Orders',
        'Total Volume',
        'Previous Period Volume',
        'Delta %',
        'Last Order Date'
      ];

      csvContent += headers.join(",") + "\r\n";

      this.reportRows.forEach((row) => {
        const rowData = [
          `"${row.buyer_name ?? ''}"`,
          row.total_order_count ?? 0,
          `"${formatCsvMoney(row.total_volume)}"`,
          `"${formatCsvMoney(row.previous_period_volume)}"`,
          `"${formatCsvDelta(row.delta_percent)}"`,
          `"${formatCsvDate(row.last_order_date)}"`
        ];

        csvContent += rowData.join(",") + "\r\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");

      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "gl_buyers_activity.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    toggleSort(column) {
      if (this.filters.sort === column) {
        this.filters.sort_direction = this.filters.sort_direction === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sort = column;
        this.filters.sort_direction = 'desc';
      }

      this.loadReportData();
    },
    sortIcon(column) {
      if (this.filters.sort !== column) {
        return '↕';
      }

      return this.filters.sort_direction === 'asc' ? '↑' : '↓';
    },
  },
  computed: {
    pageTitle() {
      return 'Buyers Activity';
    },
    maxDate() {
      return moment().format('YYYY-MM-DD');
    },
    startDate() {
      return moment(this.filters.start_date).format('L');
    },
    endDate() {
      return moment(this.filters.end_date).format('L');
    },
    reportColumns() {
      return [
        {
          key: "buyer_name",
          label: "Buyer",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "total_order_count",
          label: "Orders",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "total_volume",
          label: "Total Volume",
          thClass: "border-top-0 sortable-column",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "previous_period_volume",
          label: "Previous Period Volume",
          thClass: "border-top-0",
          tdClass: "align-middle",
          formatter: value => getFormattedPrice(value || 0)
        },
        {
          key: "delta_percent",
          label: "Delta %",
          thClass: "border-top-0 sortable-column",
          tdClass: "align-middle",
          formatter: value => this.formatDelta(value)
        },
        {
          key: "last_order_date",
          label: "Last Order Date",
          thClass: "border-top-0 sortable-column",
          tdClass: "align-middle",
          formatter: value => this.formatDate(value)
        }
      ];
    },
    reportRows() {
      if (!Array.isArray(this.report_data)) {
        return [];
      }

      return this.report_data.map((item) => ({
        buyer_id: item.buyer_id ?? null,
        buyer_name: item.buyer_name ?? 'Unknown',
        total_order_count: Number(item.total_order_count ?? 0),
        total_volume: Number(item.total_volume ?? 0),
        previous_period_volume: Number(item.previous_period_volume ?? 0),
        delta_percent: item.delta_percent,
        last_order_date: item.last_order_date ?? null,
      }));
    }
  },
  watch: {
    "filters.start_date": function (startDate) {
      if (startDate === moment().startOf('month').format('YYYY-MM-DD')) {
        startDate = null;
      }

      this.setQueryVar('start_date', startDate);
    },

    "filters.end_date": function (endDate) {
      if (endDate === moment().format('YYYY-MM-DD')) {
        endDate = null;
      }

      this.setQueryVar('end_date', endDate);
    },

    "filters.sort": function (sort) {
      if (sort === 'volume') {
        sort = null;
      }

      this.setQueryVar('sort', sort);
    },

    "filters.sort_direction": function (sortDirection) {
      if (sortDirection === 'desc') {
        sortDirection = null;
      }

      this.setQueryVar('sort_direction', sortDirection);
    },
  },
  created() {
    this.loadReportData();
  }
}
</script>

<style scoped>
.report-sort-head {
  cursor: pointer;
  user-select: none;
  color: inherit;
  text-decoration: none;
}

.report-sort-head:hover,
.report-sort-head:focus {
  text-decoration: none;
  color: inherit;
  outline: none;
}
</style>